import { useSelector } from "react-redux";
import { Tooltip, useTheme } from "@mui/material";
import React, { useState } from "react";

import { Check as CheckIcon, Remove as RemoveIcon } from "@mui/icons-material";

import cuid from "cuid";

import formatDate from "utils/formatDate";
import { isValidArray } from "utils/array";
import { checkHasMultipleRpmManufacturers } from "utils/rpmDevice.util";

import CustomTable from "components/common/table/CustomTable";
import { useFetchPatientReportsQuery } from "app/store/patientProfile/reports/api/patientProfileReportApi";
import useDebounce from "app/hooks/useDebounce";
import { getUniqueCodesWithSeparator } from "utils/billingCode.util";
import { rowStyle } from "components/pages/transmissions/table/transmissionTableRowStyle";

const PatientReport = () => {
  const theme = useTheme();
  const role = localStorage.getItem("role");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { patientSerialNumber, selectedTab, profile } = useSelector(
    (state) => state.transmission
  );

  const [search, setSearch] = useState("");
  const { data, isLoading, isFetching } = useFetchPatientReportsQuery(
    {
      patientSerialNumber,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search,
    },
    { refetchOnMountOrArgChange: true }
  );

  const onSearchChange = useDebounce((value) => {
    setSearch(value || undefined);
  }, 800);

  return (
    <div className="patient-modal__section">
      <div className="patient-form__row">
        <div className="patient-form__col--full">
          <CustomTable
            columns={[
              {
                header: "Name",
                accessorKey: "name",
                filterVariant: "text",
              },
              {
                header: "Report Date",
                accessorKey: "report_date",
                filterVariant: "text",
                accessorFn: (rowData) =>
                  formatDate(new Date(rowData?.report_date)),
                Cell: ({ cell }) => cell.getValue(),
              },
              {
                header: "Report Billing Date",
                accessorKey: "report_billing_date",
                filterVariant: "text",
                Cell: ({ row }) => {
                  const rowData = row?.original;
                  return rowData?.last_billing_date
                    ? formatDate(rowData?.last_billing_date, "MM/DD/YYYY")
                    : "-";
                },
              },
              {
                header: "Account",
                accessorKey: "account",
                filterVariant: "text",
                Cell: ({ cell }) => (
                  <div className="d-flex" style={{ width: "120px" }}>
                    {cell.getValue()}
                  </div>
                ),
              },
              {
                header: "Manufacturer",
                accessorKey: "manufacturer",
                Cell: ({ row }) => {
                  const rowData = row?.original;

                  const manufacturers = [
                    ...new Set([
                      rowData?.manufacturer,
                      ...(rowData?.additionalManufacturer || []),
                    ]),
                  ];

                  const hasMultipleRpmManufacturers =
                    checkHasMultipleRpmManufacturers(manufacturers);

                  return hasMultipleRpmManufacturers ? (
                    <Tooltip
                      title={
                        <ul className="trans-table__alertList">
                          {manufacturers?.map((manufacturer) => (
                            <li key={manufacturer}>{manufacturer}</li>
                          ))}
                        </ul>
                      }
                      placement="bottom"
                    >
                      <p>Evoke RPM</p>
                    </Tooltip>
                  ) : (
                    <p>{rowData?.manufacturer}</p>
                  );
                },
              },
              {
                header: "Device",
                accessorKey: "device",
                filterVariant: "text",
              },
              // {
              //   header: "Letter Status",
              //   accessorKey: "letter",
              //   filterVariant: "text",
              //   muiTableHeadCellProps: {
              //     align: "center",
              //   },
              //   enableHiding:
              //     profile?.account !== "Pueblo Cardiology Associates",
              //   Cell: ({ row }) => {
              //     const letter = row?.original?.letter;

              //     let letterStatusMessage = letter?.message;
              //     const mappedStatusMessage =
              //       LETTER_STATUS_MESSAGE_MAPPER[letter?.status];

              //     if (mappedStatusMessage) {
              //       letterStatusMessage += `. ${mappedStatusMessage}`;
              //     }

              //     return (
              //       <LetterStatus
              //         status={letter?.status}
              //         message={letterStatusMessage}
              //       />
              //     );
              //   },
              // },
              {
                header: "Alert",
                accessorKey: "alert",
                filterVariant: "text",
                Cell: ({ row }) => {
                  const rowData = row?.original;

                  let alertHtml = null;
                  const evokeNotes = rowData?.reportNotes?.evoke_notes;
                  const evoke_notes = evokeNotes?.length
                    ? evokeNotes?.filter(
                        (item) =>
                          item?.content?.length > 0 && item?.note?.length > 0
                      )
                    : [];

                  if (Array.isArray(rowData?.alert_text)) {
                    alertHtml = (
                      <ul className="trans-table__alertList">
                        {rowData?.alert_text?.map((item) => (
                          <li key={cuid()}>{item}</li>
                        ))}
                      </ul>
                    );
                  } else {
                    alertHtml = <div>{rowData?.alert_text}</div>;
                  }

                  const html = (
                    <div style={{ whiteSpace: "pre-line", margin: "10px" }}>
                      <div style={{ fontWeight: "bold" }}>Evoke Notes</div>
                      {evoke_notes?.map((item) => {
                        return (
                          <div key={cuid()}>
                            <div style={{ marginTop: "10px" }}>
                              {item?.note}:
                            </div>
                            <ul
                              className="trans-table__alertList"
                              style={{ marginLeft: "16px" }}
                            >
                              {item?.content?.map((content) => {
                                return (
                                  <li key={cuid()}>{content?.description}</li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  );

                  const clinicHtml = (
                    <div style={{ margin: "10px" }}>
                      {rowData?.clinic_notes?.length !== 0 && (
                        <div style={{ whiteSpace: "pre-line" }}>
                          {rowData?.account} Notes - {rowData?.clinic_notes}
                        </div>
                      )}
                      {evoke_notes?.length > 0 && (
                        <div
                          style={{ whiteSpace: "pre-line", marginTop: "15px" }}
                        >
                          <div style={{ fontWeight: "bold" }}>
                            Evoke Notes:-
                          </div>
                          {evoke_notes?.map((item) => {
                            return (
                              <div key={cuid()}>
                                <div style={{ marginTop: "10px" }}>
                                  {item?.note}:
                                </div>
                                <ul
                                  className="trans-table__alertList"
                                  style={{ marginLeft: "16px" }}
                                >
                                  {item?.content?.map((content) => {
                                    return (
                                      <li key={cuid()}>
                                        {content?.description}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );

                  const generalColumn = (
                    <Tooltip
                      style={{ fontSize: "18px" }}
                      title={evoke_notes?.length > 0 ? html : alertHtml}
                      placement="bottom"
                    >
                      <div
                        style={{
                          width: "80px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {evoke_notes?.length > 0 ? (
                          evoke_notes
                            ?.map((item) =>
                              item?.content?.map(
                                (content) => content?.description
                              )
                            )
                            ?.join(", ")
                        ) : Array.isArray(rowData?.alert_text) ? (
                          <span>{rowData?.alert_text[0]}</span>
                        ) : (
                          <span>{rowData?.alert_text}</span>
                        )}
                      </div>
                    </Tooltip>
                  );

                  const clinicColumn = (
                    <Tooltip
                      style={{ fontSize: "18px" }}
                      title={
                        rowData?.clinic_notes?.length || evoke_notes?.length
                          ? clinicHtml
                          : alertHtml
                      }
                      placement="bottom"
                    >
                      <div
                        style={{
                          width: "80px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rowData?.clinic_notes?.length !== 0 && (
                          <span>{rowData?.clinic_notes}</span>
                        )}
                        {rowData?.clinic_notes?.length === 0 &&
                          evoke_notes?.length !== 0 && (
                            <span>
                              {evoke_notes
                                ?.map((item) =>
                                  item?.content?.map(
                                    (content) => content?.description
                                  )
                                )
                                ?.join(", ")}
                            </span>
                          )}
                        {rowData?.clinic_notes?.length === 0 &&
                        evoke_notes?.length === 0 &&
                        Array.isArray(rowData?.alert_text) ? (
                          <span>{rowData?.alert_text[0]}</span>
                        ) : (
                          <span>{rowData?.alert_text}</span>
                        )}
                      </div>
                    </Tooltip>
                  );

                  const renderedColumn =
                    role === "clinic" ? clinicColumn : generalColumn;
                  return renderedColumn;
                },
              },
              {
                header: "Billing Code",
                accessorKey: "billing_code",
                filterVariant: "text",
                Cell: ({ row }) => {
                  const rowData = row?.original;
                  return isValidArray(rowData?.billingDiagnosisCodes)
                    ? getUniqueCodesWithSeparator(
                        rowData?.billingDiagnosisCodes,
                        "billingCode"
                      )
                    : rowData?.billing_code || "-";
                },
              },
              {
                header: "Billable",
                accessorKey: "billable",
                type: "boolean",
                Cell: ({ cell }) =>
                  cell?.getValue() ? <CheckIcon /> : <RemoveIcon />,
              },
            ]}
            data={data?.reports ?? []}
            options={{
              enableTopToolbar: true,
              enableSorting: false,
              rowCount: data?.count || 0,
              defaultColumn: {
                size: 100,
              },
              state: {
                isLoading: isLoading || isFetching,
                showGlobalFilter: true,
                globalFilter: search,
                pagination,
                columnVisibility: {
                  letter:
                    profile?.account !== "Pueblo Cardiology Associates"
                      ? false
                      : true,
                },
              },
              manualPagination: true,
              onGlobalFilterChange: onSearchChange,
              onPaginationChange: setPagination,
              rowsPerPageOptions: [10, 25, 100],
              renderEmptyRowsFallback: () => undefined,
              renderTopToolbarCustomActions: () => (
                <div style={{ marginLeft: "5px" }}>
                  <div style={{ fontSize: "21px", fontWeight: 500 }}>
                    Patient Reports
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "400",
                      marginTop: "5px",
                    }}
                  >
                    The reports will open in a new window
                  </div>
                </div>
              ),
              muiTableBodyRowProps: ({ row }) => {
                const rowData = row?.original;

                return {
                  onClick: () => {
                    const href = `/patient/report/${rowData?._id}`;
                    var a = document.createElement("a");
                    a.href = href;
                    a.setAttribute("target", "_blank");
                    a.click();
                  },
                  sx: {
                    cursor: "pointer",
                    "&.MuiTableRow-root": rowStyle(
                      rowData,
                      role,
                      selectedTab,
                      theme
                    ),
                    "&.MuiTableRow-root.Mui-selected td:after": {
                      background: "none !important",
                    },
                    "td:nth-of-type(3)": {
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    },
                    "td:nth-of-type(3):hover": {
                      background: "rgba(0, 0, 0, 0.05)",
                    },
                  },
                };
              },
              muiSearchTextFieldProps: {
                onPointerCancel: () => {
                  onSearchChange("");
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PatientReport;
